import {
  Asset,
  Attribute,
  id as idFn,
  Id,
  Invisible,
  ModuleBlueprint,
  ModuleVariant,
  MaterialChange,
  Translation,
  vectorFromRaw,
  VisualChange,
  XAttachmentPoint,
  YAttachmentPoint,
} from '@ess/jg-rule-executor';
import { ToJsonInputTranslatable } from '@src/app/model/to-json-input-translatable';
import {
  IdAttributeStringInputMap,
  ModuleVariantInput,
  ValueStringInputMap,
  ValueStringInputPair,
  VariantTranslationKey,
  VisualChangeInput,
  VisualChangeTypes,
} from '@src/app/generated/globalTypes';
import { sku, SKU } from '@src/app/model/sku';
import {
  GetVariantsByBlueprintId_variantsByBlueprintId,
  GetVariantsByBlueprintId_variantsByBlueprintId_attributeValues,
  GetVariantsByBlueprintId_variantsByBlueprintId_translations_values,
  GetVariantsByBlueprintId_variantsByBlueprintId_visualChanges,
} from '@src/app/generated/GetVariantsByBlueprintId';
import { PortalAsset } from '@src/app/model/portal-asset';

export class PortalModuleVariant extends ModuleVariant implements ToJsonInputTranslatable {
  skus: SKU[];

  constructor(
    id?: Id<ModuleVariant>,
    blueprintId?: Id<ModuleBlueprint>,
    name?: string,
    translationKeys?: Map<string, string>,
    catalogIcon?: Asset,
    visualChanges?: VisualChange[],
    attributeValues?: Map<Id<Attribute>, string>,
    showInCatalog?: boolean,
    groupId?: string,
    featureImage?: Asset,
  ) {
    super(
      id,
      blueprintId,
      name,
      translationKeys,
      0.0,
      catalogIcon,
      visualChanges,
      attributeValues,
      showInCatalog,
      groupId,
      undefined, // not relevant in portal, will be calculated when interacting with the configurator
      featureImage,
    );
  }

  static fromGql(gql: GetVariantsByBlueprintId_variantsByBlueprintId): PortalModuleVariant {
    const portalModuleVariant = new PortalModuleVariant(
      gql.id,
      gql.blueprintId,
      gql.name,
      PortalModuleVariant.keysFromGql(gql.translations.values),
      gql.catalogIcon,
      gql.visualChanges.map(PortalModuleVariant.toVisualChanges),
      PortalModuleVariant.attributeValuesToMap(gql.attributeValues),
      gql.showInCatalog,
      gql.groupId,
      gql.featureImage,
    );
    portalModuleVariant.skus = gql.skus.map(sku);
    portalModuleVariant.name = gql.name;
    return portalModuleVariant;
  }

  toGqlInput(): ModuleVariantInput {
    return {
      id: this.id ?? null,
      skus: this.skus?.map(x => x as unknown as string) ?? [],
      visualChanges: PortalModuleVariant.visualChangesToInput(this.visualChanges),
      name: this.name ?? null,
      attributeValues: PortalModuleVariant.attributeValuesToGql(this.attributeValues),
      showInCatalog: this.showInCatalog ?? true,
      translations: PortalModuleVariant.variantTranslationsToGqlInput(this.translationKeys),
      groupId: this.groupId,
      featureImage: (this.featureImage ? PortalAsset.fromAsset(this.featureImage).toGqlInput() : null),
    };
  }

  static toJsonInput(variantInputs: ModuleVariantInput[]): JSON {
    return { variants: variantInputs } as object as JSON;
  }

  /**
   * Format attributeValues to known gql object
   */
  private static attributeValuesToGql(attributeValues: Map<Id<Attribute>, string>): IdAttributeStringInputMap {
    if (attributeValues) {
      return {
        values: Array.from(attributeValues.entries()).map(([key, value]) => ({ key, value })),
      };
    } else {
      return {
        values: [{
          key: 'ATTR_ID',
          value: 'ATTR_VALUE (str or num)',
        }],
      };
    }
  }

  private static attributeValuesToMap(
    input: GetVariantsByBlueprintId_variantsByBlueprintId_attributeValues
  ): Map<Id<Attribute>, string> {
    return input.values.reduce(
      (acc, cur) => acc.set(cur.key, cur.value),
      new Map<Id<Attribute>, string>()
    );
  }

  private static keyValueListToMap<K, V>(list: { key: K, value: V }[]): Map<K, V> {
    return list.reduce((map, kv) => {
      map.set(kv.key, kv.value);
      return map;
    }, new Map<K, V>());
  }

  private static mapToKvList<K, V>(map: Map<K, V>): { key: K, value: V }[] {
    return Array.from(map.entries()).reduce((acc, kv) => [...acc, { key: kv[0], value: kv[1] }], []);
  }

  private static visualChangesToInput(input: VisualChange[]): VisualChangeInput[] {
    return input.map((vc) => {
      switch (vc.type) {
        case 'invisible':
          return {
            type: VisualChangeTypes.Invisible,
            invisible: {
              name: vc.name,
            },
          };
        case 'materialchange':
          return {
            type: VisualChangeTypes.MaterialChange,
            materialchange: {
              changes: { values: this.mapToKvList(vc.changes) },
            },
          };
        case 'translation':
          return {
            type: VisualChangeTypes.Translation,
            translation: {
              xaps: vc.xaps,
              yaps: vc.yaps,
              translateModule: vc.translateModule,
              translate: { x: vc.translate.x, y: vc.translate.y, z: vc.translate.z },
            },
          };
      }
    });
  }

  private static variantTranslationsToGqlInput(t: Map<string, string>): ValueStringInputMap {
    if (t) {
      return {
        values: Array.from(t).map(([k, v]) => PortalModuleVariant.variantTranslationToGqlInput(k, v)),
      };
    } else {
      return {
        values: [
          {
            key: VariantTranslationKey.Title,
            value: 'A title',
          },
          {
            key: VariantTranslationKey.Description,
            value: 'A description',
          }],
      };
    }
  }

  private static variantTranslationToGqlInput(k: string, v: string): ValueStringInputPair {
    return {
      key: PortalModuleVariant.stringToVariantKey(k),
      value: v,
    };
  }

  private static stringToVariantKey(sk: string): VariantTranslationKey {
    switch (sk) {
      case 'Description':
        return VariantTranslationKey.Description;
      case 'Title':
        return VariantTranslationKey.Title;
    }
  }

  private static keysFromGql(
    gql: GetVariantsByBlueprintId_variantsByBlueprintId_translations_values[]
  ): Map<string, string> {
    return new Map(gql.map(k => ([k.key, k.value])));
  }

  private static toVisualChanges(input: GetVariantsByBlueprintId_variantsByBlueprintId_visualChanges
  ): VisualChange {
    switch (input.__typename) {
      case 'Translation':
        return new Translation(
          input.xaps.map(x => idFn<XAttachmentPoint>(x)),
          input.yaps.map(y => idFn<YAttachmentPoint>(y)),
          vectorFromRaw(input.translate),
          input.translateModule
        );
      case 'Invisible':
        return new Invisible(
          input.name
        );
      case 'MaterialChange':
        return new MaterialChange(
          PortalModuleVariant.keyValueListToMap(input.changes.values)
        );
    }
  }
}
